import { Alert, Gap, Input, Modal, Span } from '@salecycle/ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlags } from 'core/app/AppContext';
import { LD_EMAIL_TRACKING_CONSENT_FLAG } from 'core/constants/EmailTrackingUnsubscribeSettings';
import useNotifications from 'core/notifications/useNotifications';
import { useTemplateEditorDispatch, useTemplateEditorState } from '../../context/UseTemplateEditorContext';
import { TemplateTypes } from '../../../template-list/constants/templateTypes';
import { useTemplateEditorSaveModal } from './TemplateEditorSaveModal';

interface IProps {
  modalOpen: boolean;
  setModalOpen(isOpen: boolean): void;
  onSave?(): void;
  onDiscard?(): void;
}

export const TemplateEditorSaveUnsavedChangesModal = ({ modalOpen, setModalOpen, onSave, onDiscard }: IProps) => {
  const { t } = useTranslation(['designs']);
  const [comment, setComment] = useState('');
  const { template } = useTemplateEditorState();
  const { showNotification } = useNotifications();
  const dispatch = useTemplateEditorDispatch();
  const { saveModal } = useTemplateEditorSaveModal();
  const { flagEnabled } = useFeatureFlags();
  const emailTrackingFeatureEnabled = flagEnabled(LD_EMAIL_TRACKING_CONSENT_FLAG);
  const globalSiteId = import.meta.env.VITE_GLOBAL_SITE_ID;

  const handleClose = () => {
    setModalOpen(false);
    setComment('');
  };

  const handleDiscard = () => {
    dispatch({
      type: 'DISCARD_CHANGES',
    });
    if (onDiscard) {
      onDiscard();
    }
    setModalOpen(false);
    setComment('');
  };

  const handleSave = () => {
    saveModal(
      comment,
      // success
      () => {
        showNotification(t('designs:designGallery.form.comment.notifications.saveTemplateSuccess'), 'normal');
        dispatch({
          type: 'SAVE_TEMPLATE',
        });
        if (onSave) {
          onSave();
        }
      },
      // error
      () => {
        showNotification(t('designs:designGallery.form.comment.notifications.saveTemplateError'), 'error');
      },
    );
    setComment('');
    setModalOpen(false);
  };

  return (
    <Modal
      heading={t('designs:designGallery.templateEditor.modals.unsavedChanges.header')}
      variation="clean"
      isOpen={modalOpen}
      onCancelButtonClicked={handleDiscard}
      onCloseButtonClicked={handleClose}
      onOkButtonClicked={handleSave}
      okButtonLabel={t('designs:designGallery.templateEditor.modals.unsavedChanges.confirm')}
      okButtonDisabled={comment === undefined || comment.length <= 0}
      cancelButtonLabel={t('designs:designGallery.templateEditor.modals.unsavedChanges.cancel')}
    >
      <Span>{t('designs:designGallery.templateEditor.modals.unsavedChanges.previewMessage')}</Span>
      <Gap bottom={10} />

      <Input
        label={t('designs:designGallery.form.comment.comment')}
        ariaLabel={t('designs:designGallery.form.comment.comment')}
        hint={t('designs:designGallery.form.comment.commentHint')}
        placeholder={t('designs:designGallery.form.comment.commentPlaceholder')}
        onChange={newComment => setComment(newComment)}
        value={comment}
      />
      {template.siteId === globalSiteId && template.type === TemplateTypes.unsubscribe && (
        <div>
          <Alert heading={t('designs:designGallery.globalUnsubscribeHeader')} variation="warning">
            <p>{t('designs:designGallery.globalUnsubscribeWarning.globalUnsubscribeEditWarning')}</p>
          </Alert>
        </div>
      )}
      {emailTrackingFeatureEnabled &&
        template.siteId === globalSiteId &&
        template.type === TemplateTypes.unsubscribeEmailTracking && (
          <div>
            <Alert heading={t('designs:designGallery.globalEmailTrackingUnsubscribeHeader')} variation="warning">
              <p>
                {t(
                  'designs:designGallery.globalEmailTrackingUnsubscribeWarning.globalEmailTrackingUnsubscribeEditWarning',
                )}
              </p>
            </Alert>
          </div>
        )}
    </Modal>
  );
};
